import { useLocalStorage } from '@reactuses/core';
import { log } from 'evlog/client';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@virtual-care/shadcn';

const DEFAULT_TIMER = 45;
const DEFAULT_RETRY_TIMER = 25;
const TIMER_INCREMENT = 10; // Increase timer by 10 seconds each attempt

export function ResendButton({
  onResend,
  id,
}: {
  onResend: () => void;
  id?: string;
}) {
  const { t } = useTranslation();
  const [retryAttempts, setRetryAttempts] = useLocalStorage(
    'resend-retry-attempts',
    0,
  );
  const [timer, setTimer] = useState(DEFAULT_TIMER);
  const disabled = timer > 0;

  useEffect(() => {
    if (timer <= 0) return;
    const timeout = setInterval(() => {
      setTimer((t) => t - 1);
    }, 1000);
    return () => clearInterval(timeout);
  }, [timer]);

  return (
    <Button
      id={id}
      type="button"
      variant={'ghost'}
      className={`h-auto cursor-pointer p-0.5 text-sm ${disabled ? 'text-muted-foreground' : 'text-foreground'}`}
      disabled={disabled}
      onClick={async () => {
        try {
          onResend();
          // Calculate the new timer based on attempts: base + (attempts * increment)
          const currentAttempts = retryAttempts || 0;
          const newTimer = DEFAULT_TIMER + currentAttempts * TIMER_INCREMENT;
          setTimer(newTimer);
          setRetryAttempts(currentAttempts + 1);
        } catch (error) {
          // On error, use retry timer + additional time based on attempts
          const currentAttempts = retryAttempts || 0;
          const errorTimer =
            DEFAULT_RETRY_TIMER + currentAttempts * TIMER_INCREMENT;
          setTimer(errorTimer);
          setRetryAttempts(currentAttempts + 1);
          log.error({ action: 'resend_otp', error });
        }
      }}
    >
      {t('auth.verifyPhone.resend')}
      {disabled && ` (${timer})`}
    </Button>
  );
}
